
import React, { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useUnifiedAuth } from '@/contexts/auth/UnifiedAuthProvider';
import { fetchJson } from '@/lib/api-client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Lock, Zap } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { SubscriptionGate } from '@/components/ui/subscription-gate';
import { getVerifyAccessCached, setVerifyAccessCached } from '@/lib/verify-access-cache';
import { addBreadcrumb } from '@/lib/secureBreadcrumbs';
import { ensureFreshAuth } from '@/lib/auth-readiness';

interface SubscriptionGatedRouteProps {
  children: React.ReactNode;
  requiredFeature: string;
  featureName: string;
  description: string;
}

const SubscriptionGatedRoute: React.FC<SubscriptionGatedRouteProps> = ({
  children,
  requiredFeature,
  featureName,
  description
}) => {
  const { user, subscriptionTier, loading: authLoading, authSettled, sessionExpired } = useUnifiedAuth();
  const { toast } = useToast();

  const requiredSubscription = useMemo(() => (
    ['mentor_feedback','coach_progress','technique_analysis'].includes(requiredFeature) ? 'premium' : 'free'
  ), [requiredFeature]);

  const shouldVerify = Boolean(requiredFeature && user && !sessionExpired);

  const { data: verificationData, isLoading: verificationLoading, error: verificationErr, refetch } = useQuery({
    queryKey: ['verify-access', user?.id || 'anon', requiredFeature],
    enabled: shouldVerify && !authLoading,
    queryFn: async ({ signal }): Promise<{ access: boolean }> => {
      addBreadcrumb('guard', 'verify-start', { feature: requiredFeature });
      const cached = getVerifyAccessCached(user!.id, requiredFeature);
      if (cached) {
        addBreadcrumb('guard', 'verify-cache-hit', { feature: requiredFeature, access: cached.access });
        return { access: cached.access };
      }

      const timeout = new Promise<never>((_, reject) => setTimeout(() => {
        addBreadcrumb('guard', 'verify-timeout', { feature: requiredFeature });
        reject(new Error('Verification timeout'))
      }, 5_000));
      const { verifyAccessResponseSchema } = await import('@/types/dto');
      const respPromise = fetchJson('verify-access', {
        method: 'POST',
        body: JSON.stringify({ requiredSubscription })
      }, verifyAccessResponseSchema);
      const data = await Promise.race([respPromise, timeout]);
      setVerifyAccessCached(user!.id, requiredFeature, !!data.access);
      addBreadcrumb('guard', 'verify-success', { access: !!data.access });
      logger.debug('verify-access complete');
      return data;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  // Slow verification fallback after 5s if still loading
  const [slowVerify, setSlowVerify] = React.useState(false);
  React.useEffect(() => {
    if (!verificationLoading) {
      setSlowVerify(false);
      return;
    }
    const t = setTimeout(() => setSlowVerify(true), 5_000);
    return () => clearTimeout(t);
  }, [verificationLoading]);

  React.useEffect(() => {
    if (verificationErr) {
      addBreadcrumb('guard', 'verify-error', { feature: requiredFeature });
      toast({ 
        title: 'Access verification failed', 
        description: 'Unable to verify your subscription status. Please try again.', 
        variant: 'destructive' 
      });
    }
  }, [verificationErr, toast]);

  if (sessionExpired) {
    return <Navigate to="/auth" replace />;
  }

  if (authLoading || (shouldVerify && verificationLoading)) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{slowVerify ? 'Verification is taking longer than expected. You can retry.' : 'Verifying access...'}</p>
          {slowVerify && (
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (shouldVerify && verificationErr) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <Lock className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-xl">Verification Error</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Unable to verify your access. Please try refreshing the page.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button 
                variant="outline"
                onClick={() => refetch()}
              >
                Retry
              </Button>
              <Button 
                className="w-full" 
                onClick={() => window.location.reload()}
              >
                Reload
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (shouldVerify && verificationData && 'access' in verificationData && verificationData.access === false) {
    // Show inline upgrade gate instead of redirect
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <SubscriptionGate feature={requiredFeature} featureName={featureName} description={description}>
          <div />
        </SubscriptionGate>
      </div>
    );
  }

  if (!shouldVerify || (verificationData && 'access' in verificationData && verificationData.access === true)) {
    return <>{children}</>;
  }

  // Fallback: if shouldVerify but no data yet and not loading, render a minimal safe loader
  return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Finalizing access verification...</p>
      </div>
    </div>
  );
};

export default SubscriptionGatedRoute;
